import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { 
  Truck, Package, MapPin, Phone, User, Clock, 
  Navigation, CheckCircle, ArrowLeft, Camera
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';

const OperatorActiveJob = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [job, setJob] = useState(null);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchActiveJob();
  }, []);

  const fetchActiveJob = async () => {
    setLoading(true);
    try {
      const response = await api.get('/operators/active-job');
      if (response.data.activeJob) {
        setJob(response.data.activeJob);
      } else {
        navigate('/operator/dashboard');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load job details',
        variant: 'destructive'
      });
      navigate('/operator/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (newStatus) => {
    setUpdating(true);
    try {
      await api.post(`/operators/jobs/${job.id}/update-status`, {
        status: newStatus,
        notes: notes || `Status updated to ${newStatus}`,
        current_location: null // In production, get actual location
      });

      toast({
        title: 'Status Updated',
        description: `Job status: ${newStatus.replace('_', ' ')}`
      });

      if (newStatus === 'delivered') {
        toast({
          title: 'Delivery Complete!',
          description: `You earned K${job.price?.toFixed(2)}`
        });
        navigate('/operator/dashboard');
      } else {
        // Refresh job data
        fetchActiveJob();
      }
      setNotes('');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update status',
        variant: 'destructive'
      });
    } finally {
      setUpdating(false);
    }
  };

  const getNextStatus = () => {
    switch (job?.status) {
      case 'accepted':
        return { status: 'pickup_en_route', label: 'On My Way to Pickup', icon: Navigation };
      case 'pickup_en_route':
        return { status: 'goods_collected', label: 'Goods Collected', icon: Package };
      case 'goods_collected':
        return { status: 'in_transit', label: 'Start Delivery', icon: Truck };
      case 'in_transit':
        return { status: 'delivered', label: 'Mark as Delivered', icon: CheckCircle };
      default:
        return null;
    }
  };

  const getStatusProgress = () => {
    const statuses = ['accepted', 'pickup_en_route', 'goods_collected', 'in_transit', 'delivered'];
    const currentIndex = statuses.indexOf(job?.status);
    return ((currentIndex + 1) / statuses.length) * 100;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  if (!job) {
    return null;
  }

  const nextStatus = getNextStatus();

  return (
    <div className="min-h-screen bg-gray-100 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-6">
        <button 
          onClick={() => navigate('/operator/dashboard')}
          className="flex items-center gap-2 text-orange-100 hover:text-white mb-3"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </button>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-orange-100">Active Delivery</p>
            <h1 className="text-2xl font-bold font-mono">{job.trackingCode}</h1>
          </div>
          <div className="text-right">
            <p className="text-sm text-orange-100">Earnings</p>
            <p className="text-2xl font-bold">K{job.price?.toFixed(2)}</p>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-4">
          <div className="h-2 bg-white/30 rounded-full overflow-hidden">
            <div 
              className="h-full bg-white transition-all duration-500"
              style={{ width: `${getStatusProgress()}%` }}
            />
          </div>
          <p className="text-sm text-orange-100 mt-1 capitalize">
            {job.status?.replace('_', ' ')}
          </p>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Route Card */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            Route Details
          </h3>

          <div className="space-y-4">
            {/* Pickup */}
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-green-500 mt-1.5" />
              <div className="flex-1">
                <p className="text-xs text-green-600 font-medium">PICKUP</p>
                <p className="font-medium">{job.pickupAddress}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    {job.sender?.name}
                  </div>
                  <a 
                    href={`tel:${job.sender?.phone}`}
                    className="flex items-center gap-1 text-sm text-green-600"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                </div>
              </div>
            </div>

            {/* Dropoff */}
            <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg">
              <div className="w-3 h-3 rounded-full bg-red-500 mt-1.5" />
              <div className="flex-1">
                <p className="text-xs text-red-600 font-medium">DELIVERY</p>
                <p className="font-medium">{job.dropoffAddress}</p>
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center gap-1 text-sm text-gray-600">
                    <User className="h-4 w-4" />
                    {job.receiver?.name}
                  </div>
                  <a 
                    href={`tel:${job.receiver?.phone}`}
                    className="flex items-center gap-1 text-sm text-red-600"
                  >
                    <Phone className="h-4 w-4" />
                    Call
                  </a>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Cargo Details */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Package className="h-5 w-5 text-orange-500" />
            Cargo Details
          </h3>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Description</span>
              <span className="font-medium">{job.cargo?.description}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Weight</span>
              <span className="font-medium">{job.cargo?.weightKg} kg</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Category</span>
              <span className={`px-2 py-0.5 rounded text-xs ${
                job.cargo?.category === 'fragile' ? 'bg-yellow-100 text-yellow-700' :
                job.cargo?.category === 'refrigerated' ? 'bg-blue-100 text-blue-700' :
                job.cargo?.category === 'hazardous' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {job.cargo?.category}
              </span>
            </div>
            {job.cargo?.specialInstructions && (
              <div className="pt-2 border-t">
                <p className="text-gray-500 text-xs">Special Instructions:</p>
                <p className="font-medium">{job.cargo.specialInstructions}</p>
              </div>
            )}
          </div>
        </Card>

        {/* Payment Info */}
        <Card className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-500">Payment Method</p>
              <p className="font-medium capitalize">{job.paymentMethod?.replace('_', ' ')}</p>
            </div>
            <div className={`px-3 py-1 rounded-full ${
              job.paymentStatus === 'completed' 
                ? 'bg-green-100 text-green-700' 
                : 'bg-yellow-100 text-yellow-700'
            }`}>
              {job.paymentStatus === 'completed' ? 'Paid' : 'Pending'}
            </div>
          </div>
        </Card>

        {/* Add Notes */}
        {nextStatus && (
          <Card className="p-4">
            <h3 className="font-semibold mb-2">Add Notes (Optional)</h3>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any notes for this status update..."
              className="mb-3"
            />
          </Card>
        )}

        {/* Action Button */}
        {nextStatus && (
          <Button 
            onClick={() => updateStatus(nextStatus.status)}
            disabled={updating}
            className={`w-full py-6 text-lg ${
              nextStatus.status === 'delivered' 
                ? 'bg-green-500 hover:bg-green-600' 
                : 'bg-orange-500 hover:bg-orange-600'
            }`}
            data-testid="update-status-btn"
          >
            {updating ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                Updating...
              </>
            ) : (
              <>
                <nextStatus.icon className="h-5 w-5 mr-2" />
                {nextStatus.label}
              </>
            )}
          </Button>
        )}

        {/* Status History */}
        <Card className="p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            Status History
          </h3>
          
          <div className="space-y-3">
            {job.statusHistory?.map((entry, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  idx === job.statusHistory.length - 1 ? 'bg-orange-500' : 'bg-gray-300'
                }`} />
                <div className="flex-1">
                  <p className="font-medium capitalize text-sm">
                    {entry.status?.replace('_', ' ')}
                  </p>
                  <p className="text-xs text-gray-500">{entry.notes}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(entry.timestamp).toLocaleString()}
                  </p>
                </div>
              </div>
            )).reverse()}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default OperatorActiveJob;
